import { Form } from 'antd';
import { SelectProps } from 'antd/lib';
import { FormInstance } from 'antd/lib/form';
import { useEffect, useMemo } from 'react';

import { Ticket } from '@/model/Ticket/Ticket';

/**
 * Hook que resuelve los móviles asociados a los tickets seleccionados
 * y los setea automáticamente en el formulario.
 */
const useTicketMachines = (
  form: FormInstance | undefined,
  ticketsSelectPropsWithInitials: SelectProps & { options?: any[] },
) => {
  const selectedTicketIds = Form.useWatch('tickets', form);
  const selectedTicketIdsNormalized = useMemo(
    () =>
      Array.isArray(selectedTicketIds)
        ? selectedTicketIds
        : selectedTicketIds !== undefined && selectedTicketIds !== null
          ? [selectedTicketIds]
          : [],
    [selectedTicketIds],
  );
  const selectedTicketOptions = useMemo(
    () =>
      (ticketsSelectPropsWithInitials.options ?? []).filter(option =>
        selectedTicketIdsNormalized.includes(option.value),
      ),
    [selectedTicketIdsNormalized, ticketsSelectPropsWithInitials.options],
  );
  const selectedTicketsResolved = useMemo(() => {
    if (!selectedTicketIdsNormalized.length) return true;

    return (
      selectedTicketOptions.length === selectedTicketIdsNormalized.length &&
      selectedTicketOptions.every(option => !!option.item)
    );
  }, [selectedTicketIdsNormalized, selectedTicketOptions]);
  const selectedTickets = useMemo(
    () => selectedTicketOptions.map(option => option.item).filter(Boolean) as Ticket[],
    [selectedTicketOptions],
  );
  // Obtención de móviles asociados a los tickets seleccionados
  const ticketMachineOptions: { value: number; label: string }[] = useMemo(() => {
    const unique = new Map<number, { value: number; label: string }>();

    selectedTickets.forEach(ticket => {
      if (!ticket.movil) return;
      if (unique.has(ticket.movil)) return;
      unique.set(ticket.movil, { value: ticket.movil, label: ticket.movil_str ?? '' });
    });

    return [...unique.values()];
  }, [selectedTickets]);

  // Seteo de los móviles asociados a los tickets en el formulario
  useEffect(() => {
    if (!selectedTicketIdsNormalized.length) {
      form?.setFieldValue('moviles', []);

      return;
    }

    if (!selectedTicketsResolved) return;

    form?.setFieldValue(
      'moviles',
      ticketMachineOptions.map(option => option.value),
    );
  }, [form, selectedTicketIdsNormalized.length, selectedTicketsResolved, ticketMachineOptions]);

  return {
    ticketMachineOptions,
    ticketMachineOptionsLoading: !selectedTicketsResolved && !!selectedTicketIdsNormalized.length,
  };
};

export default useTicketMachines;
